import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AssetLocationTypes, KeyManagementTypes } from "@/logic/api";
import BarcodeScanner from "@/components/thirdParty/react-barcode-reader";
import LocationPickerDialog from "@/components/pickers/locationPicker/LocationPickerDialog";
import { useScanningFlow } from "../hooks/useScanningFlow";
import ScanStatusBox from "./ScanStatusBox";
import ManualInput from "./ManualInput";

const locationSelectionTypes = [
  AssetLocationTypes.Lane,
  AssetLocationTypes.Zone,
  AssetLocationTypes.User,
  AssetLocationTypes.DynamicBox,
  AssetLocationTypes.Hook,
  AssetLocationTypes.KeyBox,
];

const locationDisplayTypes = [
  AssetLocationTypes.Zone,
  AssetLocationTypes.Lane,
  AssetLocationTypes.Users,
  AssetLocationTypes.User,
  AssetLocationTypes.Keyroom,
  AssetLocationTypes.Hook,
  AssetLocationTypes.KeyBox,
  AssetLocationTypes.DynamicBox,
];

const ScanningInterface: React.FC = () => {
  const { t } = useTranslation();
  const {
    state,
    tasks,
    expectKeyScanBeforeLocation,
    handleScan,
    handleLocationPicked,
    handleTaskSelected,
    reset,
  } = useScanningFlow();

  const [showLocationPicker, setShowLocationPicker] = useState(false);

  return (
    <Box
      data-testid="scanning-page"
      sx={{
        maxWidth: { xs: "100%", xl: 1200 },
        margin: "0 auto",
        padding: 3,
        paddingX: { xs: 3, xl: 6 },
      }}
    >
      <Box sx={{ textAlign: "center", paddingY: 3, marginBottom: 2 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: 600 }}
        >
          {t("scanning.title")}
        </Typography>
      </Box>

      <Box sx={{ marginBottom: 3 }}>
        <ScanStatusBox state={state} expectKeyScanBeforeLocation={expectKeyScanBeforeLocation} />
      </Box>

      <Box sx={{ marginBottom: 3 }}>
        {state.status !== "processing" && (
          <BarcodeScanner
            onScan={handleScan}
            onError={() => {}}
            minLength={1}
          />
        )}

        <ManualInput
          state={state}
          tasks={tasks}
          expectKeyScanBeforeLocation={expectKeyScanBeforeLocation}
          onScan={handleScan}
          onSelectLocation={() => setShowLocationPicker(true)}
          onTaskSelected={handleTaskSelected}
          onCancel={reset}
        />
      </Box>

      <LocationPickerDialog
        title={t("scanning.manual.selectLocation")}
        show={showLocationPicker}
        displayTypes={locationDisplayTypes}
        selectionTypes={locationSelectionTypes}
        keyManagementTypes={[KeyManagementTypes.box, KeyManagementTypes.hook]}
        onChange={(loc) => {
          setShowLocationPicker(false);
          if (loc) {
            handleLocationPicked(loc);
          }
        }}
        onCancel={() => setShowLocationPicker(false)}
      />
    </Box>
  );
};

export default ScanningInterface;
